import React, { useState, useEffect } from "react";
import "../styles/SubmitMusicModal.css";
import { submitMusicToFirestore } from "../services/SubmitMusicService";
import { sortedStates, sortedRegions } from "../utils/StaticFormData";

interface SubmitMusicModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SubmitMusicModal = ({
  isOpen,
  onClose,
}: SubmitMusicModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStateID, setSelectedStateID] = useState<number | "">("");
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    instagram: "",
    location: "",
    songLink: "",
  });

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStateID = parseInt(e.target.value);
    setSelectedStateID(newStateID);
    setFormData((prev) => ({ ...prev, location: "" }));
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRegionID = parseInt(e.target.value);
    const selectedStateObj = sortedStates.find(
      (s) => s.stateID === selectedStateID,
    );
    const selectedRegionObj = sortedRegions.find(
      (r) => r.regionID === newRegionID,
    );

    if (selectedStateObj && selectedRegionObj) {
      setFormData((prev) => ({
        ...prev,
        location: `${selectedRegionObj.regionName}, ${selectedStateObj.stateName}`,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitMusicToFirestore(formData);

      setFormData({
        instagram: "",
        location: "",
        songLink: "",
      });
      setSelectedStateID("");

      setShowSuccess(true);
    } catch (error) {
      alert("Error submitting track. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableRegions = sortedRegions.filter(
    (region) => region.stateID === selectedStateID,
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content submit-music-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {showSuccess ? (
          <div className="success-view">
            <div className="success-header">
              <span className="success-subtitle">
                Music Submission Received
              </span>
            </div>

            <div className="success-content">
              <h3 className="success-heading">
                Thank You for Submitting Music to Augio
              </h3>
              <p>We appreciate you sharing your music with us.</p>
              <p>
                Our team will review your submission, and if your music is
                selected to be added to the platform, we’ll reach out with more
                details about when it will go live on Augio.
              </p>
              <button
                type="button"
                className="submit-btn success-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="modal-header-section">
              <h2 className="modal-title">Submit Your Track</h2>
              <p className="modal-subtitle">Get discovered on Augio</p>
            </div>

            <form onSubmit={handleSubmit} className="submit-form">
              <div className="form-group">
                <label htmlFor="instagram">Artist Instagram</label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  placeholder="Instagram @"
                  value={formData.instagram}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label>State / Region</label>

                <div className="location-row">
                  <select
                    id="stateSelect"
                    value={selectedStateID}
                    onChange={handleStateChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="" disabled>
                      Select State
                    </option>
                    {sortedStates.map((state) => (
                      <option key={state.stateID} value={state.stateID}>
                        {state.stateName}
                      </option>
                    ))}
                  </select>

                  <select
                    id="regionSelect"
                    value={
                      formData.location
                        ? availableRegions.find((r) =>
                            formData.location.startsWith(r.regionName),
                          )?.regionID || ""
                        : ""
                    }
                    onChange={handleRegionChange}
                    required
                    disabled={!selectedStateID || isSubmitting}
                  >
                    <option value="" disabled>
                      {selectedStateID ? "Select Region" : "Select State First"}
                    </option>
                    {availableRegions.map((region) => (
                      <option key={region.regionID} value={region.regionID}>
                        {region.regionName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="songLink">Song Link</label>
                <input
                  type="url"
                  id="songLink"
                  name="songLink"
                  placeholder="Spotify, AppleMusic, etc"
                  value={formData.songLink}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Submit Track"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
